/* eslint-disable @typescript-eslint/no-explicit-any */
import { DateTimeFormatter, LocalTime, TemporalAccessor } from '@js-joda/core';
import _ from 'lodash';

export interface TockDateTimeFormatter {
  setShortDisplayTimeFormat?(displayTimeAsShort: boolean): void;
  format(temporal: TemporalAccessor, showAmPm?: boolean): string;
}

class TwelveHourFormatter {
  static numberPattern = DateTimeFormatter.ofPattern('h:mm');
  static noon = LocalTime.parse('12:00');
  displayTimeAsShort: boolean;

  constructor() {
    this.displayTimeAsShort = false;
  }

  getAmPm(temporal: TemporalAccessor): string {
    return LocalTime.from(temporal).isBefore(TwelveHourFormatter.noon) ? 'AM' : 'PM';
  }

  format(temporal: TemporalAccessor, showAmPm = true): string {
    if (!this.displayTimeAsShort) {
      return `${TwelveHourFormatter.numberPattern.format(temporal)} ${
        showAmPm ? this.getAmPm(temporal) : ''
      }`;
    } else {
      // This has no space and just shows 'P' or 'M'
      return `${TwelveHourFormatter.numberPattern.format(temporal)}${this.getAmPm(temporal).substr(
        0,
        1
      )}`;
    }
  }
  setShortDisplayTimeFormat(displayTimeAsShort: any) {
    this.displayTimeAsShort = displayTimeAsShort;
  }
}

function createTockDatetimeFormatter(pattern: string): TockDateTimeFormatter {
  const formatter = DateTimeFormatter.ofPattern(pattern);
  return {
    format(temporal: TemporalAccessor, showAmPm?: boolean) {
      return formatter.format(temporal);
    },
  };
}

export const twelveHourFormatter = new TwelveHourFormatter();
export const twentyFourHourFormatter = createTockDatetimeFormatter(`HH':'mm`);
export const twentyFourHourDotFormatter = createTockDatetimeFormatter(`HH'.'mm`);
export const twentyFourHourNakedFormatter = createTockDatetimeFormatter(`HHmm`);
export const unknownFormatter = twentyFourHourFormatter;

export const timeFormattersByLocale: { [key: string]: TockDateTimeFormatter } = {
  'ar-ae': twelveHourFormatter,
  'ar-il': unknownFormatter,
  'ar-ma': unknownFormatter,
  'bg-bg': twentyFourHourFormatter,
  'cs-cz': twentyFourHourFormatter,
  'da-dk': twentyFourHourDotFormatter,
  'de-at': twentyFourHourFormatter,
  'de-ch': twentyFourHourFormatter,
  'de-de': twentyFourHourFormatter,
  'de-lu': twentyFourHourFormatter,
  'el-gr': twelveHourFormatter,
  'en-au': twelveHourFormatter,
  'en-bm': twelveHourFormatter,
  'en-ca': twelveHourFormatter,
  'en-gb': twentyFourHourFormatter,
  'en-gg': twentyFourHourFormatter,
  'en-gy': twentyFourHourFormatter,
  'en-ie': twentyFourHourFormatter,
  'en-il': unknownFormatter,
  'en-ky': twelveHourFormatter,
  'en-nz': twelveHourFormatter,
  'en-us': twelveHourFormatter,
  'es-ar': twentyFourHourFormatter,
  'es-cl': twentyFourHourFormatter,
  'es-co': twentyFourHourFormatter,
  'es-cr': twentyFourHourFormatter,
  'es-do': twentyFourHourFormatter,
  'es-ec': twentyFourHourFormatter,
  'es-es': twentyFourHourFormatter,
  'es-gt': twentyFourHourFormatter,
  'es-hn': twelveHourFormatter,
  'es-mx': twentyFourHourFormatter,
  'es-pa': twentyFourHourFormatter,
  'es-pr': twentyFourHourFormatter,
  'es-pe': twentyFourHourFormatter,
  'es-sv': twentyFourHourFormatter,
  'fi-fi': twentyFourHourFormatter,
  'fr-be': twentyFourHourFormatter,
  'fr-ca': unknownFormatter,
  'fr-ch': twentyFourHourFormatter,
  'fr-fr': twentyFourHourFormatter,
  'he-il': unknownFormatter,
  'hi-in': twelveHourFormatter,
  'hr-hr': twelveHourFormatter,
  'hu-hu': twentyFourHourFormatter,
  'it-ch': twentyFourHourFormatter,
  'it-it': twentyFourHourFormatter,
  'ja-jp': twentyFourHourFormatter,
  'ka-ge': unknownFormatter,
  'ko-kr': twentyFourHourFormatter,
  'lv-lv': twentyFourHourFormatter,
  'ms-my': unknownFormatter,
  'nb-no': twentyFourHourFormatter,
  'nl-be': twentyFourHourFormatter,
  'nl-nl': twentyFourHourFormatter,
  'pl-pl': twentyFourHourFormatter,
  'pt-br': twentyFourHourFormatter,
  'pt-pt': twentyFourHourFormatter,
  'ro-ro': twentyFourHourFormatter,
  'ru-ru': twentyFourHourFormatter,
  'sl-si': twentyFourHourFormatter,
  'sq-al': twelveHourFormatter,
  'sv-se': twentyFourHourFormatter,
  'sw-ke': unknownFormatter,
  'th-th': twentyFourHourFormatter,
  'vi-vn': twentyFourHourFormatter,
  'zh-hans-hk': twentyFourHourFormatter,
  'zh-hans-sg': unknownFormatter,
};

export function getTimeFormatter(locale: string): TockDateTimeFormatter {
  const timeFormatter: TockDateTimeFormatter =
    timeFormattersByLocale[_.toLower(locale)] || unknownFormatter;
  if (!_.isFunction(timeFormatter.setShortDisplayTimeFormat)) {
    return timeFormatter;
  } else {
    timeFormatter.setShortDisplayTimeFormat(false);
    return timeFormatter;
  }
}

export function getShortTimeFormatter(locale: string): TockDateTimeFormatter {
  const timeFormatter = timeFormattersByLocale[_.toLower(locale)] || unknownFormatter;
  if (!_.isFunction(timeFormatter.setShortDisplayTimeFormat)) {
    return timeFormatter;
  } else {
    timeFormatter.setShortDisplayTimeFormat(true);
    return timeFormatter;
  }
}

export function usesTwentyFourHourTimes(locale: string): boolean {
  const timeFormatter = timeFormattersByLocale[_.toLower(locale)] || unknownFormatter;
  return timeFormatter === twentyFourHourFormatter;
}
